import { useState, useEffect } from 'react';
import { getMe, createCheckout, getMyWins, getMemberSettings, updateEntries } from '../api';
import { colours as c, font, gbp, shortDate, monthYear } from '../theme';

/* ---------- small helpers ------------------------------------------------ */

const Skeleton = ({ width = '100%', height = '20px', style = {} }) => (
  <div className="skeleton" style={{ width, height, ...style }} />
);

const LoadingSkeleton = () => (
  <div style={S.page} className="pad-page">
    <div style={S.card}>
      <Skeleton width="180px" height="14px" style={{ marginBottom: 16 }} />
      <Skeleton width="320px" height="40px" style={{ marginBottom: 12 }} />
      <Skeleton width="280px" height="18px" />
    </div>
    <div style={S.card}>
      <Skeleton width="120px" height="20px" style={{ marginBottom: 16 }} />
      <Skeleton height="16px" style={{ marginBottom: 8 }} />
      <Skeleton width="80%" height="16px" />
    </div>
  </div>
);

// Split bar — widths come from the live prize percentages
const SplitBar = ({ pct }) => {
  const p = pct || { first: 40, second: 25, third: 15, retained: 20 };
  return (
    <div style={{ display: 'flex', height: 9, borderRadius: 2, overflow: 'hidden', gap: 2 }}>
      <div style={{ width: `${p.first}%`, background: c.teal }} />
      <div style={{ width: `${p.second}%`, background: c.tealSoft }} />
      <div style={{ width: `${p.third}%`, background: c.sageSoft }} />
      <div style={{ width: `${Math.max(0, p.retained)}%`, background: c.sage }} />
    </div>
  );
};

// Entry stepper. Plain -/+ buttons rather than a number input: on mobile a
// number field opens a keyboard and invites typing values the cap rejects.
const EntryStepper = ({ value, max, onChange, disabled }) => (
  <div style={S.stepper}>
    <button
      type="button"
      aria-label="One fewer entry"
      style={{ ...S.stepBtn, ...(value <= 1 || disabled ? S.stepBtnOff : {}) }}
      disabled={value <= 1 || disabled}
      onClick={() => onChange(value - 1)}
    >
      −
    </button>
    <span style={S.stepValue} aria-live="polite">
      {value} {value === 1 ? 'entry' : 'entries'}
    </span>
    <button
      type="button"
      aria-label="One more entry"
      style={{ ...S.stepBtn, ...(value >= max || disabled ? S.stepBtnOff : {}) }}
      disabled={value >= max || disabled}
      onClick={() => onChange(value + 1)}
    >
      +
    </button>
  </div>
);

/* ---------- date helpers ------------------------------------------------- */

// The draw runs 9am on the 1st, for the month that just ended.
const nextDrawInfo = () => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1, 9, 0, 0);
  const coveredMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const days = Math.ceil((next - now) / 86400000);

  // Headline: "Tomorrow, 9am" / "12 days away"
  let when;
  if (days <= 0) when = 'Today, 9am';
  else if (days === 1) when = 'Tomorrow, 9am';
  else when = `${days} days away`;

  // Sentence form: "Join before 9am tomorrow" / "Join in the next 12 days"
  let joinBy;
  if (days <= 0) joinBy = 'before 9am today';
  else if (days === 1) joinBy = 'before 9am tomorrow';
  else joinBy = `in the next ${days} days`;

  return {
    when,
    joinBy,
    dateLine: next.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
    coveredMonth: coveredMonth.toLocaleDateString('en-GB', { month: 'long' }),
  };
};

/* ---------- component ---------------------------------------------------- */

export default function Dashboard({ member, onLogout }) {
  const [profile, setProfile] = useState(null);
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentType, setPaymentType] = useState('annual');

  // Entry selection for the join flow, and the cap that bounds it.
  const [entries, setEntries] = useState(1);
  const [maxEntries, setMaxEntries] = useState(1);

  // Changing entries on an existing monthly subscription.
  const [editingEntries, setEditingEntries] = useState(false);
  const [draftEntries, setDraftEntries] = useState(1);
  const [savingEntries, setSavingEntries] = useState(false);
  const [entriesNote, setEntriesNote] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [meRes, winsRes] = await Promise.all([getMe(), getMyWins()]);
        setProfile(meRes.data);
        setWins(winsRes.data.wins);

        const held = Math.max(1, Number(meRes.data?.payment?.entries) || 1);
        setDraftEntries(held);
      } catch (err) {
        // handled by interceptor
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // The cap is a separate call so a settings failure never blocks the
  // dashboard. If it fails the stepper stays at 1 and checkout still works.
  useEffect(() => {
    getMemberSettings()
      .then(res => setMaxEntries(Math.max(1, Number(res.data?.settings?.max_entries_per_member) || 1)))
      .catch(() => setMaxEntries(1));
  }, []);

  const handlePayment = async () => {
    setPaying(true);
    try {
      const res = await createCheckout(paymentType, entries);
      window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.error || 'Payment failed');
      setPaying(false);
    }
  };

  const handleSaveEntries = async () => {
    setSavingEntries(true);
    setEntriesNote('');
    try {
      const res = await updateEntries(draftEntries);
      setEntriesNote(res.data?.message || `Your entries will change to ${draftEntries} at your next payment.`);
      setEditingEntries(false);
    } catch (err) {
      setEntriesNote(err.response?.data?.error || 'Entries could not be changed.');
    } finally {
      setSavingEntries(false);
    }
  };

  const payment = profile?.payment;
  const pot = profile?.pot;
  const isActive = payment && new Date(payment.coverage_end) >= new Date();
  const isAnnual = payment?.payment_type === 'annual';
  const draw = nextDrawInfo();

  // Entries currently held, and what the chosen plan costs at that count.
  const heldEntries = Math.max(1, Number(payment?.entries) || 1);
  const annualTotal = 36 * entries;
  const monthlyTotal = 3 * entries;

  const totalWon = wins.reduce((s, w) => s + Number(w.prize || 0), 0);
  const firstName = (member?.name || '').split(' ')[0];

  // Renewal = day after cover ends
  const renewsOn = payment
    ? (() => { const d = new Date(payment.coverage_end); d.setDate(d.getDate() + 1); return d; })()
    : null;

  return (
    <div style={S.shell}>
      {/* ---------- header ---------- */}
      <header style={S.header} className="pad-header">
        <div style={S.logoWrap}>
          <img src="/logo-bpfa.jpg" alt="BPFA" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <span style={S.brand}>The 100 Club</span>
          <span style={S.brandSub}>Burwash Playing Fields Association</span>
        </div>
        <span style={S.hello} className="hide-mobile">Hello, {firstName}</span>
        <button className="btn-ghost" style={S.signOut} onClick={onLogout}>Sign out</button>
      </header>

      {loading ? <LoadingSkeleton /> : (
        <div style={S.page} className="pad-page">

          {/* ---------- top grid ---------- */}
          <div style={S.topGrid} className="grid-2">

            {/* status card */}
            {isActive ? (
              <div style={S.card}>
                <div style={S.statusRow}>
                  <span style={{ ...S.dot, background: c.sage }} />
                  <span style={S.statusTextOk}>You're in this month's draw</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={S.bigHead}>All paid up{firstName ? `, ${firstName}` : ''}</span>
                  <span style={S.lead}>
                    Your membership covers you to{' '}
                    <strong style={{ color: c.ink }}>{shortDate(payment.coverage_end)}</strong>.
                    Nothing to do — just wait for the draw.
                  </span>
                </div>

                <div style={S.factRow} className="stack-sm">
                  <div style={S.fact}>
                    <span style={S.factLabel}>Your plan</span>
                    <span style={S.factValue}>
                      {isAnnual
                        ? `Annual · ${gbp(36 * heldEntries)} a year`
                        : `Monthly · ${gbp(3 * heldEntries)} a month`}
                    </span>
                  </div>
                  <div style={S.fact}>
                    <span style={S.factLabel}>Your entries</span>
                    <span style={S.factValue}>
                      {heldEntries} {heldEntries === 1 ? 'entry' : 'entries'}
                    </span>
                  </div>
                  <div style={S.fact}>
                    <span style={S.factLabel}>{isAnnual ? 'Renews' : 'Next payment'}</span>
                    <span style={S.factValue}>{renewsOn ? shortDate(renewsOn) : '—'}</span>
                  </div>
                  <div style={S.fact}>
                    <span style={S.factLabel}>Member since</span>
                    <span style={S.factValue}>
                      {profile?.member?.created_at ? monthYear(profile.member.created_at) : '—'}
                    </span>
                  </div>
                </div>

                {/* Entries can only be changed on a live Stripe subscription.
                    Annual members have already paid for the year, so theirs
                    change at renewal. */}
                {isAnnual ? (
                  <span style={S.small}>
                    {heldEntries === 1
                      ? 'You hold one entry. You can take more when your membership renews.'
                      : `You hold ${heldEntries} entries, each one a separate chance in every monthly draw. You can change that when your membership renews.`}
                  </span>
                ) : editingEntries ? (
                  <div style={S.entriesEdit}>
                    <span style={S.eyebrow}>How many entries?</span>
                    <EntryStepper
                      value={draftEntries}
                      max={maxEntries}
                      onChange={setDraftEntries}
                      disabled={savingEntries}
                    />
                    <span style={S.small}>
                      {gbp(3 * draftEntries)} a month from your next payment.
                      {draftEntries >= maxEntries && maxEntries > 1
                        ? ` ${maxEntries} is the most any one member can hold.`
                        : ''}
                    </span>
                    <div style={S.entriesBtnRow}>
                      <button
                        style={S.entriesSave}
                        onClick={handleSaveEntries}
                        disabled={savingEntries || draftEntries === heldEntries}
                      >
                        {savingEntries ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        style={S.entriesCancel}
                        onClick={() => { setEditingEntries(false); setDraftEntries(heldEntries); }}
                        disabled={savingEntries}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={S.entriesRow}>
                    <span style={S.small}>
                      {heldEntries === 1
                        ? 'One entry in every monthly draw.'
                        : `${heldEntries} entries, each a separate chance in every monthly draw.`}
                    </span>
                    {maxEntries > 1 && (
                      <button
                        style={S.entriesLink}
                        onClick={() => { setEditingEntries(true); setDraftEntries(heldEntries); setEntriesNote(''); }}
                      >
                        Change
                      </button>
                    )}
                  </div>
                )}

                {entriesNote && <span style={S.entriesNote}>{entriesNote}</span>}
              </div>
            ) : (
              <div style={S.cardWarn}>
                <div style={S.statusRow}>
                  <span style={{ ...S.dot, background: '#c1712f' }} />
                  <span style={S.statusTextWarn}>Not in this month's draw</span>
                </div>
                <span style={S.bigHeadSm}>
                  {payment
                    ? `Your cover ran out on ${shortDate(payment.coverage_end)}`
                    : 'You haven\u2019t joined yet'}
                </span>
                <span style={{ ...S.lead, color: '#6b4a2f' }}>
                  Join <strong>{draw.joinBy}</strong> and you'll be in
                  the {draw.coveredMonth} draw.
                </span>

                <span style={{ ...S.eyebrow, paddingTop: 6 }}>Choose how you'd like to pay</span>

                <div
                  style={paymentType === 'annual' ? S.optionOn : S.option}
                  onClick={() => setPaymentType('annual')}
                >
                  <span style={paymentType === 'annual' ? S.radioOn : S.radioOff} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                    <div style={S.optionTop}>
                      <span style={S.optionName}>Annual</span>
                      <span style={S.optionPrice}>{gbp(annualTotal)}</span>
                    </div>
                    <span style={S.optionDesc}>
                      One payment, covers 12 months. Same £3 an entry each month
                      into the pot.
                    </span>
                  </div>
                </div>

                <div
                  style={paymentType === 'monthly' ? S.optionOn : S.option}
                  onClick={() => setPaymentType('monthly')}
                >
                  <span style={paymentType === 'monthly' ? S.radioOn : S.radioOff} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                    <div style={S.optionTop}>
                      <span style={S.optionName}>Monthly</span>
                      <span style={S.optionPrice}>{gbp(monthlyTotal)}</span>
                    </div>
                    <span style={S.optionDesc}>Spread the cost, cancel any time.</span>
                  </div>
                </div>

                {maxEntries > 1 && (
                  <div style={S.entriesPick}>
                    <div style={S.entriesPickTop}>
                      <span style={S.eyebrow}>How many entries?</span>
                      <EntryStepper value={entries} max={maxEntries} onChange={setEntries} />
                    </div>
                    <span style={S.optionDesc}>
                      Every entry is £3 a month into the pot and a separate
                      chance in the draw. You can hold up to {maxEntries}.
                    </span>
                  </div>
                )}

                <button style={S.payBtn} onClick={handlePayment} disabled={paying}>
                  {paying
                    ? 'Redirecting…'
                    : paymentType === 'annual'
                      ? `Pay ${gbp(annualTotal)} and join`
                      : `Start ${gbp(monthlyTotal)} a month`}
                </button>
                <span style={S.payNote}>Payment is handled securely by Stripe.</span>
              </div>
            )}

            {/* right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div style={S.drawCard}>
                <span style={S.eyebrowOnTeal}>Next draw</span>
                <span style={S.drawWhen}>{draw.when}</span>
                <span style={S.drawSub}>
                  {draw.dateLine}, for the month of {draw.coveredMonth}. Winners are
                  emailed the same morning.
                </span>
              </div>

              <div style={{ ...S.card, flex: 1, gap: 14, padding: '24px 28px' }}>
                <span style={S.eyebrow}>{pot ? `${pot.month}'s pot` : 'This month\u2019s pot'}</span>
                <span style={S.potFigure}>{gbp(pot?.total)}</span>
                <span style={S.small}>
                  {pot
                    ? `${pot.entryCount ?? pot.memberCount} ${(pot.entryCount ?? pot.memberCount) === 1 ? 'entry' : 'entries'} × ${gbp(pot.monthlyRate)}, across ${pot.memberCount} member${pot.memberCount === 1 ? '' : 's'}. Three prizes of ${gbp(pot.first)}, ${gbp(pot.second)} and ${gbp(pot.third)} — the remaining ${gbp(pot.retained)} goes to the playing fields.`
                    : 'The pot grows with every entry taken.'}
                </span>
                <div style={{ marginTop: 'auto' }}>
                  <SplitBar pct={pot?.percents} />
                </div>
              </div>
            </div>
          </div>

          {/* ---------- wins ---------- */}
          <div style={S.winsCard}>
            <div style={S.winsHead}>
              <span style={S.winsTitle}>My wins</span>
              {wins.length > 0 && (
                <span style={S.small}>{gbp(totalWon)} won since you joined</span>
              )}
            </div>

            {wins.length === 0 ? (
              <div style={S.emptyWins}>
                <span style={S.emptyTitle}>No wins yet</span>
                <span style={S.small}>
                  Good luck in the next draw — three names come out every month.
                </span>
              </div>
            ) : (
              <>
                <div style={S.winsHeadRow} className="hide-mobile">
                  <span>Draw</span><span>Place</span>
                  <span style={{ textAlign: 'right' }}>Prize</span>
                </div>
                {wins.map((w, i) => (
                  <div key={i} style={{ ...S.winRow, borderBottom: i === wins.length - 1 ? 'none' : `1px solid ${c.hairSoft}` }}>
                    <span style={S.winMonth}>{monthYear(w.draw_date)}</span>
                    <span style={w.place === '1st' ? S.winPlaceFirst : S.winPlace}>
                      {w.place} place
                    </span>
                    <span style={S.winPrize}>{gbp(w.prize)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- styles ------------------------------------------------------- */

const S = {
  shell: { minHeight: '100vh', background: c.paper, fontFamily: font.sans },

  header: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '18px 40px', background: c.teal,
  },
  logoWrap: {
    width: 44, height: 44, borderRadius: '50%', overflow: 'hidden',
    background: '#fff', flex: 'none',
  },
  brand: { fontFamily: font.serif, fontSize: 21, color: c.onTeal, lineHeight: 1.1 },
  brandSub: {
    fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: c.onTealDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  hello: { fontSize: 15, color: c.onTealSub },
  signOut: {
    padding: '9px 15px', border: '1px solid #6d8887', background: 'transparent',
    color: c.onTeal, borderRadius: 3, fontFamily: font.sans, fontSize: 14,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },

  page: {
    flex: 1, padding: '34px 40px 40px', display: 'flex',
    flexDirection: 'column', gap: 24, maxWidth: 1440, margin: '0 auto', width: '100%',
  },
  topGrid: {
    display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)', gap: 24,
  },

  card: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: '32px 34px', display: 'flex', flexDirection: 'column', gap: 20,
  },
  cardWarn: {
    background: '#fdf4e9', border: '1px solid #ecd6b8', borderRadius: 3,
    padding: '28px 30px', display: 'flex', flexDirection: 'column', gap: 14,
  },

  statusRow: { display: 'flex', alignItems: 'center', gap: 12 },
  dot: { width: 12, height: 12, borderRadius: '50%', flex: 'none' },
  statusTextOk: {
    fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: c.sageDeep, fontWeight: 600,
  },
  statusTextWarn: {
    fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: c.lapsedFg, fontWeight: 600,
  },

  bigHead: { fontFamily: font.serif, fontSize: 46, lineHeight: 1.05, color: c.ink },
  bigHeadSm: { fontFamily: font.serif, fontSize: 30, lineHeight: 1.1, color: c.ink },
  lead: { fontSize: 17, color: c.body, lineHeight: 1.5 },
  small: { fontSize: 14, color: c.body, lineHeight: 1.5 },
  eyebrow: {
    fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.muted,
  },
  eyebrowOnTeal: {
    fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: c.onTealDim,
  },

  factRow: {
    display: 'flex', gap: 1, background: c.hairline,
    border: `1px solid ${c.hairline}`, borderRadius: 3, overflow: 'hidden',
  },
  fact: {
    flex: 1, background: c.paper, padding: '16px 18px',
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  factLabel: { fontSize: 12, color: c.muted },
  factValue: { fontSize: 17, color: c.ink },

  drawCard: {
    background: c.teal, borderRadius: 3, padding: '26px 28px',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  drawWhen: { fontFamily: font.serif, fontSize: 38, color: c.onTeal, lineHeight: 1.05 },
  potFigure: {
    fontFamily: font.serif, fontSize: 42, lineHeight: 0.95, color: c.ink,
    fontVariantNumeric: 'tabular-nums',
  },
  drawSub: { fontSize: 15, color: '#cfdcd6', lineHeight: 1.5 },

  option: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: '18px 20px', display: 'flex', alignItems: 'flex-start', gap: 14,
    cursor: 'pointer',
  },
  optionOn: {
    background: c.card, border: `2px solid ${c.teal}`, borderRadius: 3,
    padding: '18px 20px', display: 'flex', alignItems: 'flex-start', gap: 14,
    cursor: 'pointer',
  },
  radioOn: {
    width: 20, height: 20, borderRadius: '50%', border: `6px solid ${c.teal}`,
    background: c.card, flex: 'none', marginTop: 3,
  },
  radioOff: {
    width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #c9c2b1',
    background: c.paper, flex: 'none', marginTop: 3,
  },
  optionTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' },
  optionName: { fontSize: 17, color: c.ink, fontWeight: 600 },
  optionPrice: { fontFamily: font.serif, fontSize: 24, color: c.ink },
  optionDesc: { fontSize: 14, color: c.body, lineHeight: 1.5 },

  // --- entries -------------------------------------------------------------
  stepper: {
    display: 'flex', alignItems: 'center', gap: 2,
    border: `1px solid ${c.hairDeep}`, borderRadius: 3,
    background: c.card, overflow: 'hidden', flex: 'none',
  },
  stepBtn: {
    width: 40, height: 40, border: 'none', background: 'transparent',
    color: c.teal, fontFamily: font.sans, fontSize: 20, lineHeight: 1,
    cursor: 'pointer', flex: 'none',
  },
  stepBtnOff: { color: '#c9c2b1', cursor: 'default' },
  stepValue: {
    minWidth: 92, textAlign: 'center', fontSize: 15, color: c.ink,
    fontVariantNumeric: 'tabular-nums',
  },

  entriesPick: {
    border: `1px solid ${c.hairline}`, borderRadius: 3, background: c.card,
    padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10,
  },
  entriesPickTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 12, flexWrap: 'wrap',
  },
  entriesRow: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    gap: 12, flexWrap: 'wrap',
  },
  entriesLink: {
    border: 'none', background: 'transparent', color: c.teal,
    fontFamily: font.sans, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', padding: 0, textDecoration: 'underline',
  },
  entriesEdit: {
    border: `1px solid ${c.hairline}`, borderRadius: 3, background: c.paper,
    padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12,
  },
  entriesBtnRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  entriesSave: {
    padding: '11px 22px', border: 'none', background: c.teal, color: c.onTeal,
    borderRadius: 3, fontFamily: font.sans, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', minHeight: 44,
  },
  entriesCancel: {
    padding: '11px 20px', background: 'transparent', color: c.teal,
    border: `1px solid ${c.hairDeep}`, borderRadius: 3,
    fontFamily: font.sans, fontSize: 14, cursor: 'pointer', minHeight: 44,
  },
  entriesNote: { fontSize: 14, color: c.sageDeep, lineHeight: 1.5 },

  payBtn: {
    padding: 17, border: 'none', background: c.ink, color: c.onTeal,
    borderRadius: 3, fontFamily: font.sans, fontSize: 16, fontWeight: 600,
    cursor: 'pointer', minHeight: 44,
  },
  payNote: { fontSize: 13, color: c.muted, lineHeight: 1.5, textAlign: 'center' },

  winsCard: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    display: 'flex', flexDirection: 'column',
  },
  winsHead: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    padding: '20px 28px 16px', borderBottom: `1px solid ${c.hairline}`, flexWrap: 'wrap', gap: 8,
  },
  winsTitle: { fontFamily: font.serif, fontSize: 22, color: c.ink },
  winsHeadRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '12px 28px',
    borderBottom: `1px solid ${c.hairSoft}`, fontSize: 11, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: c.muted,
  },
  winRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
    padding: '15px 28px', alignItems: 'center',
  },
  winMonth: { fontSize: 16, color: c.ink },
  winPlace: { fontSize: 16, color: c.body },
  winPlaceFirst: { fontSize: 16, color: c.sageDeep, fontWeight: 600 },
  winPrize: {
    fontSize: 16, color: c.ink, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
  },
  emptyWins: {
    padding: '28px', display: 'flex', flexDirection: 'column',
    gap: 7, alignItems: 'center', textAlign: 'center',
  },
  emptyTitle: { fontFamily: font.serif, fontSize: 19, color: c.ink },

};
